/**
 * Desbloqueo del Vault: pide la passphrase y guarda el token (15 min) en memoria.
 *
 * `useUnlockPrompt()` devuelve `ensureUnlocked()` — una promesa que resuelve
 * true cuando hay token vigente (ya sea porque estaba o porque la persona lo
 * acaba de ingresar) y false si canceló. Las pantallas lo llaman antes de
 * revelar / crear.
 */
import { useCallback, useRef, useState, type FormEvent } from 'react';
import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { errMsg, useVaultMutations, useVaultSession } from '@/hooks/useVault';
import { getVaultUnlockToken } from '@/lib/vault-session';

interface UnlockDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUnlocked: () => void;
}

export function UnlockDialog({ open, onOpenChange, onUnlocked }: UnlockDialogProps) {
  const { t } = useTranslation();
  const { unlock } = useVaultMutations();
  const [passphrase, setPassphrase] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await unlock.mutateAsync({ passphrase });
      setPassphrase('');
      onOpenChange(false);
      onUnlocked();
    } catch (err) {
      setError(errMsg(err as { message?: string }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setPassphrase(''); setError(null); } onOpenChange(o); }}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-4 w-4" />
              {t('vault.unlock.title', 'Desbloquear el Vault')}
            </DialogTitle>
            <DialogDescription>
              {t('vault.unlock.body', 'Ingresá tu passphrase del Vault. Queda desbloqueado 15 minutos en esta pestaña.')}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-2">
            <Label htmlFor="vault-passphrase">{t('vault.unlock.label', 'Passphrase')}</Label>
            <Input
              id="vault-passphrase"
              type="password"
              autoComplete="current-password"
              autoFocus
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
            />
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.ui.cancel', 'Cancelar')}
            </Button>
            <Button type="submit" disabled={!passphrase || unlock.isPending} data-tour="unlock-submit">
              {unlock.isPending ? t('vault.unlock.working', 'Verificando…') : t('vault.unlock.cta', 'Desbloquear')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- hook pegado al dialog a propósito (mismo estado); Fast Refresh solo pierde estado local en dev
export function useUnlockPrompt() {
  const { unlocked } = useVaultSession();
  const [open, setOpen] = useState(false);
  const resolver = useRef<((ok: boolean) => void) | null>(null);
  const pending = useRef<Promise<boolean> | null>(null);

  const ensureUnlocked = useCallback((): Promise<boolean> => {
    if (getVaultUnlockToken()) return Promise.resolve(true);
    // Dos llamadas seguidas (doble click) comparten la misma promesa: la
    // primera no queda colgada (review F3a).
    if (pending.current) return pending.current;
    pending.current = new Promise<boolean>((resolve) => {
      resolver.current = (ok) => {
        pending.current = null;
        resolve(ok);
      };
      setOpen(true);
    });
    return pending.current;
  }, []);

  const onOpenChange = (o: boolean) => {
    setOpen(o);
    if (!o && resolver.current) {
      resolver.current(!!getVaultUnlockToken());
      resolver.current = null;
    }
  };

  const dialog = <UnlockDialog open={open} onOpenChange={onOpenChange} onUnlocked={() => { resolver.current?.(true); resolver.current = null; }} />;
  return { ensureUnlocked, unlocked, dialog };
}
